// filepath: apps/web/src/auth/AuthGuard.tsx
//
// FR-UI-CORE-2 AuthGuard component.
//
// Wraps the authenticated app shell. While the OIDC client is
// loading the session (or during sign-in), renders a
// LoadingScreen. When the user is unauthenticated, triggers the
// OIDC Authorization Code + PKCE flow (signinRedirect). When the
// user is authenticated, renders children.
//
// Non-responsibilities:
//   - Bootstrap error screens (handled by App composition root).
//   - The /oidc-callback route (handled by App composition root).
//   - The 401-triggered silent refresh (handled by silentRefresh.ts).

import React from "react";
import { useAuth } from "react-oidc-context";
import { LoadingScreen } from "./bootstrapErrorStates";

export interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({
  children,
}: AuthGuardProps): React.ReactElement | null {
  const auth = useAuth();

  // FR-UI-CORE-2: kick the OIDC Authorization Code + PKCE flow
  // when the user is not authenticated. The effect runs after the
  // tree mounts (avoiding React's "update during render" warning)
  // and is gated internally on the auth state, so we MUST call
  // it unconditionally on every render — Rules of Hooks require
  // the same hooks in the same order across renders. The previous
  // implementation called `useEffect` only inside the
  // `!auth.isAuthenticated` branch, which React flagged as
  // "change in the order of Hooks" and which broke subsequent
  // renders (the second-mount OIDC callback path).
  React.useEffect(() => {
    if (auth.isLoading) return;
    if (auth.isAuthenticated) return;
    void auth.signinRedirect().catch(() => {
      // The OIDC client surfaces errors via auth.error which
      // is observed by the App composition root.
    });
  }, [auth.isLoading, auth.isAuthenticated, auth]);

  // When the SPA is mid-callback, the OIDC client is in `loading`
  // state — the library is exchanging the auth code for tokens.
  // Render the LoadingScreen (AC-5) so the user has visual feedback.
  if (auth.isLoading) {
    return <LoadingScreen />;
  }

  // Unauthenticated: render LoadingScreen while the post-mount
  // effect (above) triggers the redirect. AuthGuard is the single
  // source of interactive sign-in triggers — LoginRoute delegates
  // to the same path.
  if (!auth.isAuthenticated || !auth.user) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}
